import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Loader2, 
  Download, 
  Upload, 
  FileCode, 
  CheckCircle, 
  AlertCircle,
  Save,
  RotateCcw
} from 'lucide-react';
import { api } from '@/utils/api';
import { useUIStore } from '@/store';
import * as yaml from 'js-yaml';

// Default YAML content
const DEFAULT_YAML = `# LLM 配置文件
# 支持配置多个不同用途的 LLM

version: "1.0"
profiles:
  - name: "Default Translator"
    purpose: translator
    model: qwen3-max
    base_url: https://navira.blog/v1
    api_key: sk-pf-webapp
    is_default: true
    temperature: 0.7
    max_tokens: 4096
    top_p: 1.0

  - name: "Default Classifier"
    purpose: classifier
    model: qwen3-max
    base_url: https://navira.blog/v1
    api_key: sk-pf-webapp
    is_default: true
    temperature: 0.3
    max_tokens: 2048
    top_p: 1.0

  - name: "Default Digest"
    purpose: digest
    model: qwen3-max
    base_url: https://navira.blog/v1
    api_key: sk-pf-webapp
    is_default: true
    temperature: 0.7
    max_tokens: 4096
    top_p: 1.0

  - name: "Default Planner"
    purpose: planner
    model: qwen3-max
    base_url: https://navira.blog/v1
    api_key: sk-pf-webapp
    is_default: true
    temperature: 0.7
    max_tokens: 4096
    top_p: 1.0

  - name: "Default Writer"
    purpose: writer
    model: qwen3-max
    base_url: https://navira.blog/v1
    api_key: sk-pf-webapp
    is_default: true
    temperature: 0.8
    max_tokens: 8192
    top_p: 1.0

  - name: "Default Search"
    purpose: search
    model: qwen3-max
    base_url: https://navira.blog/v1
    api_key: sk-pf-webapp
    is_default: true
    temperature: 0.7
    max_tokens: 4096
    top_p: 1.0
`;

// Purpose options for validation
const VALID_PURPOSES = ['translator', 'classifier', 'digest', 'planner', 'writer', 'search', 'vision'];

interface ValidationError {
  line: number;
  message: string;
}

export function LLMProfilesYAML() {
  const [yamlContent, setYamlContent] = useState(DEFAULT_YAML);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isValid, setIsValid] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useUIStore();

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async ({ content, replaceAll }: { content: string; replaceAll: boolean }) => {
      const blob = new Blob([content], { type: 'application/x-yaml' });
      const formData = new FormData();
      formData.append('file', blob, 'llm-profiles.yaml');
      formData.append('replace_all', replaceAll ? 'true' : 'false');
      
      const response = await api.post('/settings/llm-profiles-yaml/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    },
  });

  // Validate YAML on content change
  useEffect(() => {
    validateYAML(yamlContent);
  }, [yamlContent]);

  const validateYAML = (content: string) => {
    const errors: ValidationError[] = [];
    
    try {
      const parsed = yaml.load(content) as any;
      
      if (!parsed || typeof parsed !== 'object') {
        errors.push({ line: 1, message: 'Invalid YAML structure' });
        setValidationErrors(errors);
        setIsValid(false);
        return;
      }

      if (!parsed.profiles || !Array.isArray(parsed.profiles)) {
        errors.push({ line: 1, message: 'Missing "profiles" array' });
      } else {
        parsed.profiles.forEach((profile: any, index: number) => {
          const line = findLineNumber(content, `name: "${profile.name}"`) || index * 10 + 5;
          
          if (!profile.name) {
            errors.push({ line, message: `Profile ${index + 1}: Missing name` });
          }
          if (!profile.purpose) {
            errors.push({ line, message: `Profile ${index + 1}: Missing purpose` });
          } else if (!VALID_PURPOSES.includes(profile.purpose)) {
            errors.push({ line, message: `Profile ${index + 1}: Invalid purpose "${profile.purpose}". Valid: ${VALID_PURPOSES.join(', ')}` });
          }
          if (!profile.model) {
            errors.push({ line, message: `Profile ${index + 1}: Missing model` });
          }
          if (!profile.base_url) {
            errors.push({ line, message: `Profile ${index + 1}: Missing base_url` });
          }
          if (!profile.api_key) {
            errors.push({ line, message: `Profile ${index + 1}: Missing api_key` });
          }
        });
      }
      
      setIsValid(errors.length === 0);
      setValidationErrors(errors);
    } catch (e: any) {
      errors.push({ line: 1, message: `YAML parse error: ${e.message}` });
      setIsValid(false);
      setValidationErrors(errors);
    }
  };

  const findLineNumber = (content: string, search: string): number | null => {
    const lines = content.split('\n');
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes(search)) {
        return i + 1;
      }
    }
    return null;
  };

  const handleImport = (replaceAll: boolean = false) => {
    if (!isValid) {
      addToast({
        title: '验证失败',
        description: '请先修复 YAML 错误',
        variant: 'destructive'
      });
      return;
    }

    importMutation.mutate(
      { content: yamlContent, replaceAll },
      {
        onSuccess: (data) => {
          addToast({
            title: '导入成功',
            description: `新增 ${data.imported} 个，更新 ${data.updated} 个配置`,
          });
          if (data.errors?.length > 0) {
            console.warn('Import errors:', data.errors);
          }
        },
        onError: (error: any) => {
          addToast({
            title: '导入失败',
            description: error.response?.data?.detail || error.message,
            variant: 'destructive'
          });
        }
      }
    );
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/settings/llm-profiles-yaml/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `llm-profiles-${new Date().toISOString().split('T')[0]}.yaml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      addToast({
        title: '导出成功',
        description: '配置文件已下载',
      });
    } catch (error: any) {
      addToast({
        title: '导出失败',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive'
      });
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.yaml') && !file.name.endsWith('.yml')) {
      addToast({
        title: '文件格式错误',
        description: '请上传 YAML 文件 (.yaml 或 .yml)',
        variant: 'destructive'
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setYamlContent(content);
      addToast({
        title: '文件已加载',
        description: `已加载 ${file.name}`,
      });
    };
    reader.readAsText(file);
  };

  const handleLoadExisting = async () => {
    try {
      const response = await api.get('/settings/llm-profiles-yaml/export', {
        responseType: 'text'
      });
      setYamlContent(response.data);
      addToast({
        title: '加载成功',
        description: '已加载现有配置',
      });
    } catch (error: any) {
      addToast({
        title: '加载失败',
        description: error.response?.data?.detail || '暂无现有配置',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileCode className="h-5 w-5" />
                YAML 配置编辑器
              </CardTitle>
              <CardDescription>
                使用 YAML 格式批量管理 LLM 配置
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadExisting}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                加载现有
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
              >
                <Download className="h-4 w-4 mr-2" />
                导出
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                上传
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".yaml,.yml"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                实时验证 YAML 格式
              </span>
            </div>
            <div className="flex items-center gap-2">
              {!isValid && (
                <span className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  有 {validationErrors.length} 个错误
                </span>
              )}
              {isValid && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  验证通过
                </span>
              )}
            </div>
          </div>

          {/* Editor */}
          <div className="border rounded-lg overflow-hidden" style={{ height: '500px' }}>
            <Editor
              height="100%"
              language="yaml"
              value={yamlContent}
              onChange={(value) => setYamlContent(value || '')}
              options={{
                minimap: { enabled: false },
                lineNumbers: 'on',
                wordWrap: 'on',
                scrollBeyondLastLine: false,
                fontSize: 14,
                tabSize: 2,
                automaticLayout: true,
              }}
              theme="vs-light"
            />
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="space-y-2">
              {validationErrors.map((error, idx) => (
                <Alert key={idx} variant="destructive" className="py-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    第 {error.line} 行: {error.message}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              支持字段: name, purpose, model, base_url, api_key, is_default, temperature, max_tokens, top_p
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => handleImport(false)}
                disabled={!isValid || importMutation.isPending}
              >
                {importMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                合并导入
              </Button>
              <Button
                onClick={() => handleImport(true)}
                disabled={!isValid || importMutation.isPending}
                variant="destructive"
              >
                {importMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                替换全部
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
